<template>
    <!-- Single-root wrapper: fixes WKWebView compositing layer for iOS gesture back -->
    <div class="newsletter-detail-root">
        <!-- Feature Disabled State (Desktop - large centered card) -->
        <div v-if="!isNewsletterModuleEnabled && !isMobile" class="feature-disabled-wrapper">
            <div class="feature-disabled-container">
                <div class="disabled-content">
                    <i class="fas fa-toggle-off fa-3x mb-3 text-primary"></i>
                    <h4>Newsletter Module Disabled</h4>
                    <p>The Newsletter Module has been disabled by System Administration. Please contact System Owner to enable this feature.</p>
                    <button class="btn-back-dashboard" @click="goBack">Back to Dashboard</button>
                </div>
            </div>
        </div>

        <template v-else-if="isMobile">
            <div class="top-background">
                <svg width="330" height="55" viewBox="0 0 330 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="111" cy="-84" rx="219" ry="139" fill="currentColor"/>
                </svg>
            </div>
            <!-- Mobile Disabled State (inline banner) -->
            <div v-if="!isNewsletterModuleEnabled" class="newsletter-detail-page mobile-view">
                <div class="back-nav" style="margin-top: 64px" @click="goBack">
                    <i class="fas fa-arrow-left me-2"></i> Back to Dashboard
                </div>
                <div class="feature-disabled-inline">
                    <i class="fas fa-toggle-off"></i>
                    <div>
                        <h4>Newsletter Module Disabled</h4>
                        <p>This feature has been disabled by your administrator.</p>
                    </div>
                </div>
            </div>
        </template>

        <div v-if="isNewsletterModuleEnabled" class="newsletter-detail-page" :class="{ 'mobile-view': isMobile, 'container': !isMobile }">
            <div class="back-nav" @click="goBack" :style="isMobile ? 'margin-top: 64px' : ''">
                <i class="fas fa-arrow-left me-2"></i> Back to Dashboard
            </div>

            <div class="content-wrapper">
                <main class="main-content" v-if="newsletter">
                    <h1 class="page-title">{{ newsletter.title }}</h1>
                    <p class="page-subtitle">{{ newsletter.summary }}</p>

                    <div class="hero-image">
                        <img :src="newsletter.cover_image || defaultLogo" alt="Hero Image">
                    </div>

                    <div class="meta-row">
                        <div class="meta-group">
                            <span class="meta-item"><i class="far fa-calendar me-2"></i> {{ formatDate(newsletter.published_at) }}</span>
                            <span class="meta-item">
                                <i class="fas fa-building me-2"></i>
                                <template v-if="newsletter.merchant_id">
                                    {{ newsletter.merchant_name }} • {{ newsletter.branch_name }}
                                </template>
                                <template v-else-if="newsletter.branch_id">
                                    {{ newsletter.branch_name }}
                                </template>
                                <template v-else>
                                    {{ newsletter.corporate_name }}
                                </template>
                            </span>
                        </div>
                    </div>

                    <div class="article-body">
                        <div v-if="newsletter.body" v-html="newsletter.body"></div>
                        <div v-else class="text-center p-4">
                            <div class="spinner-border text-primary" role="status"></div>
                        </div>
                    </div>
                </main>

                <!-- Loading spinner when no newsletter in cache yet -->
                <main class="main-content" v-else-if="loading">
                    <div class="text-center p-5">
                        <div class="spinner-border text-primary" role="status"></div>
                    </div>
                </main>

                <!-- Newsletter not found / access denied -->
                <main class="main-content" v-else>
                    <no-items message="Newsletter not found" transparent />
                </main>

                <aside class="sidebar-menu">
                    <div class="sidebar-widget card-style">
                        <h3 class="widget-heading">
                            <i class="fas fa-bookmark me-2" style="color: var(--primary-color, #ef4444);"></i> Other Newsletters
                        </h3>
                        <div class="related-list">
                            <div
                                v-for="item in relatedNewsletters"
                                :key="item.id"
                                class="related-item"
                                :class="{ 'is-unread': !item.is_read }"
                                @click="goToDetail(item.id)"
                            >
                                <div class="related-content">
                                    <div class="related-thumb">
                                        <img :src="item.cover_image || defaultLogo" alt="">
                                    </div>
                                    <div class="related-info">
                                        <h4 class="related-title" :title="item.title">{{ truncate(item.title, 50) }}</h4>
                                        <p class="related-publisher">
                                            <template v-if="item.merchant_id">
                                                {{ item.merchant_name }} • {{ item.branch_name }}
                                            </template>
                                            <template v-else-if="item.branch_id">
                                                {{ item.branch_name }}
                                            </template>
                                            <template v-else>
                                                {{ item.corporate_name }}
                                            </template>
                                        </p>
                                        <span class="related-date">{{ formatDate(item.published_at) }}</span>
                                    </div>
                                </div>
                                <img src="/img/member-icon/arrow-right.svg" alt="" class="related-chevron">
                                <div v-if="!item.is_read" class="related-badge">
                                    <span>New</span>
                                </div>
                            </div>
                            <p v-if="relatedNewsletters.length === 0" class="text-muted small">No newsletters found.</p>
                        </div>
                        <button class="view-all-btn" @click="goToAll">View All Newsletter</button>
                    </div>

                    <!-- Promo Card -->
                    <div class="promo-card" v-if="nextTier">
                        <h3 class="promo-title">Member Exclusive</h3>
                        <p class="promo-desc">Upgrade to {{ nextTier.name }} and get an extra benefit only for you</p>
                        <button class="promo-btn" @click="goToBenefit">Upgrade Now</button>
                        <div class="promo-bg-icon"><i class="fas fa-star"></i></div>
                    </div>
                </aside>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import NoItems from '@/components/member/NoItems.vue'

export default {
    name: 'MemberNewsletterDetail',
    components: {
        NoItems
    },
    setup() {
        const store = useStore()
        const route = useRoute()
        const router = useRouter()

        // Synchronous cache lookup — ensures first render has data (no blank on gesture back)
        const routeId = route.params.id
        const initialCached = routeId
            ? store.getters['ClientNewsletter/getNewsletterById'](routeId)
            : null

        const loading = ref(initialCached ? !initialCached.body : true)
        const currentArticle = ref(initialCached || null)
        let activeRequestId = null


        const newsletter = computed(() => {
            const currentRouteId = route.params.id
            if (!currentRouteId) return null

            if (currentArticle.value && String(currentArticle.value.id) === String(currentRouteId)) {
                return currentArticle.value
            }
            const fromCache = store.getters['ClientNewsletter/getNewsletterById'](currentRouteId)
            if (fromCache) {
                return fromCache
            }
            const fromStore = store.state.ClientNewsletter.currentNewsletter
            if (fromStore && String(fromStore.id) === String(currentRouteId)) {
                return fromStore
            }
            return null
        })

        const allNewsletters = computed(() => store.state.ClientNewsletter.newsletters)

        // Check if newsletter module is enabled via feature flag
        const isNewsletterModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.newsletterModule?.flag || 'ACTIVE') === 'ACTIVE'
        })
        
        const defaultLogo = computed(() => {
            const user = store.state.Auth.currUser || store.state.Auth.userInfo
            return user?.member?.corporate_logo || '/img/member-icon/tier-logo.svg'
        })
        
        const relatedNewsletters = computed(() => {
             if (!allNewsletters.value) return []
             const currentId = newsletter.value?.id || route.params.id
             let list = [...allNewsletters.value].filter(n => String(n.id) !== String(currentId))
             
             // Sort: Unread (is_read: false) first, then by Date (published_at) Descending
             list.sort((a, b) => {
                 if (a.is_read !== b.is_read) {
                     return a.is_read ? 1 : -1
                 }
                 return new Date(b.published_at) - new Date(a.published_at)
             })
             
             return list.slice(0, 3)
        })

        const benefitData = computed(() => store.state.ClientBenefit.dashboard)
        
        const nextTier = computed(() => {
            const tiers = benefitData.value?.tier || []
            const currentTierName = benefitData.value?.tier_name
            if (tiers.length === 0 || !currentTierName) return null
            
            const currentIndex = tiers.findIndex(t => t.name === currentTierName)
            if (currentIndex === -1 || currentIndex === tiers.length - 1) return null
            
            return tiers[currentIndex + 1]
        })

        const loadData = async () => {
             const requestedId = route.params.id
             if (!requestedId) return

             activeRequestId = requestedId

             const cached = store.getters['ClientNewsletter/getNewsletterById'](requestedId)
             if (cached) {
                 currentArticle.value = cached
                 loading.value = !cached.body
             } else {
                 // No full cache — try lightweight list item as placeholder (shows title/cover immediately)
                 const listItem = store.state.ClientNewsletter.newsletters?.find(
                     n => String(n.id) === String(requestedId)
                 ) || null
                 currentArticle.value = listItem  // null if truly unknown, listItem if in sidebar list
                 loading.value = true             // body still loading
             }

             try {
                 const resp = await store.dispatch('ClientNewsletter/readById', requestedId)
                 
                 if (String(route.params.id) !== String(requestedId) || activeRequestId !== requestedId) {
                     return
                 }

                 if (resp?.data?.data) {
                     currentArticle.value = resp.data.data
                 }
                 loading.value = false
                 store.dispatch('ClientNewsletter/markAsRead', requestedId)
             } catch (err) {
                 // STALE ERROR GUARD
                 if (String(route.params.id) !== String(requestedId) || activeRequestId !== requestedId) {
                     return
                 }
                 console.warn('Newsletter not accessible:', err?.response?.data?.message || err.message)

                 const isNotFoundOrForbidden = err?.response?.status === 404 || err?.response?.status === 403
                 if (!cached || isNotFoundOrForbidden) {
                     currentArticle.value = null
                 }
                 loading.value = false
             }
             
             // Always load other newsletters for sidebar
             store.dispatch('ClientNewsletter/read', { limit: 5 })
        }
        
        onMounted(() => {
             loadData()
             store.dispatch('ClientBenefit/read')
        })
        
        watch(() => route.params.id, (newId) => {
             if (newId) {
                 loadData()
             }
        })
        
        const isMobile = ref(window.innerWidth < 992)

        const onResize = () => {
            isMobile.value = window.innerWidth < 992
        }
        window.addEventListener('resize', onResize)
        onBeforeUnmount(() => {
            window.removeEventListener('resize', onResize)
        })

        const goBack = () => {
             const previousRoute = window.history.state?.back
             if (previousRoute && (previousRoute === '/client/dashboard' || previousRoute.startsWith('/client/dashboard'))) {
                 router.back()
             } else {
                 router.push('/client/dashboard')
             }
        }
        
        const goToDetail = (id) => {
             store.dispatch('ClientNewsletter/markAsRead', id)
             router.push(`/client/newsletters/${id}`)
        }
        
        const goToAll = () => {
             router.push(`/client/newsletters`)
        }

        const goToBenefit = () => {
             router.push('/client/benefit')
        }
        
        const formatDate = (date) => {
             if (!date) return ''
             return new Date(date).toLocaleDateString('en-US', {
                 month: 'short',
                 day: '2-digit',
                 year: 'numeric'
             })
        }
        
        const truncate = (text, length = 25) => {
            if (!text) return ''
            if (text.length <= length) return text
            return text.substring(0, length) + '..'
        }
        
        return {
            newsletter,
            loading,
            relatedNewsletters,
            goBack,
            goToDetail,
            goToAll,
            formatDate,
            isMobile,
            truncate,
            nextTier,
            goToBenefit,
            defaultLogo,
            isNewsletterModuleEnabled
        }
    }
}
</script>

<style scoped>
/* Single-root wrapper — transparent, no layout side effects */
.newsletter-detail-root {
    display: contents;
}

/* Feature Disabled State */
.feature-disabled-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px;
    width: 100%;
    min-height: 100vh;
}

.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0 0 1rem;
    font-size: 0.95rem;
}

.btn-back-dashboard {
    background: var(--primary-color, #ef4444);
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: opacity 0.2s;
}

.btn-back-dashboard:hover {
    opacity: 0.9;
}

/* Feature Disabled Inline State (Mobile) */
.feature-disabled-inline {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background: #f9fafb;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    margin: 12px 0;
}

.feature-disabled-inline i {
    font-size: 24px;
    color: #9ca3af;
    flex-shrink: 0;
}

.feature-disabled-inline h4 {
    font-size: 15px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 4px;
}

.feature-disabled-inline p {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
}

.newsletter-detail-page {
    max-width: 1440px;
    margin: 0 auto;
    position: relative;
    padding-left: 24px;
    padding-right: 24px;
}

.top-background {
    position: absolute;
    top: 0;
    left: 0;
    color: var(--primary-color);
}

.back-nav {
    color: #6b7280;
    cursor: pointer;
    font-size: 14px;
    margin-bottom: 24px;
    display: inline-flex;
    align-items: center;
}

.back-nav:hover {
    color: #111827;
}

.main-title {
    font-family: 'serif'; /* Or matching font */
    font-size: 36px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 32px;
    line-height: 1.2;
}

.content-wrapper {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 24px;
    align-items: start;
}

@media (max-width: 992px) {
    .content-wrapper {
        grid-template-columns: 1fr;
    }

    .newsletter-detail-page {
        padding: 0;
    }
}

/* Main Content */
.main-content {
    background: white;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.page-title {
    font-size: 28px;
    font-weight: 600;
    color: #111827;
    margin-top: 0;
    margin-bottom: 8px;
    overflow-wrap: break-word;
    word-break: break-word;
}

.page-subtitle {
    font-size: 14px;
    color: #6B7280;
    margin-bottom: 24px;
    overflow-wrap: break-word;
    word-break: break-word;
}

@media (max-width: 900px) {

    .main-content {
        padding: 20px;
        box-shadow: none;
    }

    .page-title {
        font-size: 20px;
        font-weight: 520;
    }

    .hero-image {
        height: auto;
    }
    
    .meta-group {
        flex-direction: column;
        gap: 8px;
    }
}

.hero-image {
    width: 100%;
    height: 400px; /* specific height */
    border-radius: 16px;
    overflow: hidden;
    margin-bottom: 24px;
    background-color: #f3f4f6;
}

.hero-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.meta-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 24px;
    margin-bottom: 24px;
}

.meta-group {
    display: flex;
    gap: 24px;
    color: #6b7280;
    font-size: 14px;
}

.article-body {
    font-size: 16px;
    color: #374151;
    overflow-wrap: break-word;
    word-break: break-word;
}

/* Sidebar */
.sidebar-menu {
    display: flex;
    flex-direction: column;
    gap: 32px;
    border: none !important;
    box-shadow: none !important;
}

.sidebar-widget {
    background: white;
    padding: 24px;
    border-radius: 16px;
    border: none !important;
    box-shadow: none !important;
}

.widget-heading {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
}

.related-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 24px;
}

.related-item {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    background-color: white;
    border-radius: 12px;
    gap: 12px;
    padding: 16px;
    cursor: pointer;
    border: 1px solid #e1e4ea79;
    transition: background-color 0.2s;
    overflow: hidden;
}

.related-item:hover {
    background-color: #f9fafb;
}

.related-content {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.related-thumb {
    width: 44px;
    min-width: 44px;
    height: 44px;
    min-height: 44px;
    background-color: white;
    border-radius: 50%;
    border: 1px solid #E1E4EA;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
}

.related-thumb img {
    margin: auto;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.related-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.related-title {
    font-size: 14px;
    font-weight: bold;
    color: #111827;
    line-height: 1.4;
    margin: 0;
    overflow-wrap: break-word;
    word-break: break-word;
}

.related-publisher {
    font-size: 12px;
    font-weight: normal;
    color: #111827;
    line-height: 100%;
    margin: 0;
}

.related-date {
    font-size: 12px;
    font-weight: normal;
    color: #99A0AE;
    line-height: 100%;
    white-space: nowrap;
}

.related-chevron {
    width: 20px;
    height: 20px;
    align-self: center;
    flex-shrink: 0;
    opacity: 0.6;
}

.related-badge {
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 38px 38px 0 0;
    border-color: var(--primary-color) transparent transparent transparent;
    z-index: 10;
}

.related-badge span {
    position: absolute;
    top: -32px;
    left: 3px;
    font-size: 8px;
    font-weight: bold;
    color: white;
    text-transform: uppercase;
    white-space: nowrap;
    line-height: normal;
    transform: rotate(-45deg);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}

.view-all-btn {
    width: 100%;
    padding: 12px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    color: #4b5563;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.2s;
}

.view-all-btn:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

/* Promo Card */
.promo-card {
    background: var(--primary-gradient, linear-gradient(135deg, #ef4444 0%, #dc2626 100%));
    color: white;
    padding: 32px;
    border-radius: 16px;
    position: relative;
    overflow: hidden;
    text-align: center;
}

.promo-title {
    font-family: serif;
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 12px;
}

.article-body :deep(p) {
    margin-bottom: 1rem;
}

.article-body :deep(ul),
.article-body :deep(ol) {
    margin-bottom: 1rem;
    padding-left: 2rem;
}

.article-body :deep(h1),
.article-body :deep(h2),
.article-body :deep(h3),
.article-body :deep(h4),
.article-body :deep(h5),
.article-body :deep(h6) {
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.article-body :deep(h1:first-child),
.article-body :deep(h2:first-child),
.article-body :deep(h3:first-child),
.article-body :deep(p:first-child) {
    margin-top: 0;
}

.article-body :deep(a) {
    color: var(--primary-color, #0d6efd);
    text-decoration: none;
}

.article-body :deep(a:hover) {
    text-decoration: underline;
}

.article-body :deep(blockquote) {
    border-left: 3px solid #d1d5db;
    padding-left: 1rem;
    margin-left: 0;
    margin-right: 0;
    color: #4b5563;
    font-style: italic;
    margin-bottom: 1rem;
}

.promo-desc {
    font-size: 14px;
    margin-bottom: 24px;
    opacity: 0.9;
    line-height: 1.5;
}

.promo-btn {
    background: white;
    color: var(--primary-color, #ef4444);
    border: none;
    padding: 10px 24px;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
}

.promo-bg-icon {
    position: absolute;
    bottom: -20px;
    right: -20px;
    font-size: 120px;
    opacity: 0.1;
    transform: rotate(15deg);
}
</style>
